import React, { useState } from 'react';
import { Dialog } from '../ui/radix/Dialog';
import { Button, Callout, Icon } from '../ui';
import { PaymentMethodType } from '../../context/WalletContext';

interface PaymentMethod {
  id: string;
  type: PaymentMethodType;
  name: string;
  last4: string;
  isDefault: boolean;
}

interface DepositDialogProps {
  open: boolean;
  onClose: () => void;
  onDeposit: (amount: number, methodId: string) => Promise<void>;
  paymentMethods: PaymentMethod[];
  processing: boolean;
  error: string | null;
  success: string | null;
}

const DepositDialog: React.FC<DepositDialogProps> = ({
  open,
  onClose,
  onDeposit,
  paymentMethods,
  processing,
  error,
  success,
}) => {
  const [amount, setAmount] = useState('');
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethodType>('card');
  const [selectedMethodId, setSelectedMethodId] = useState<string | null>(null);

  const handleDeposit = async () => {
    const depositAmount = parseFloat(amount);
    if (isNaN(depositAmount) || depositAmount <= 0) return;

    let methodId: string | undefined;
    if (selectedMethodId) {
      methodId = selectedMethodId;
    } else {
      const defaultMethod = paymentMethods.find(m => m.type === selectedMethod && m.isDefault);
      const anyMethod = paymentMethods.find(m => m.type === selectedMethod);
      methodId = defaultMethod?.id || anyMethod?.id;
    }
    if (methodId) await onDeposit(depositAmount, methodId);
  };

  const handleClose = () => {
    setAmount('');
    setSelectedMethodId(null);
    onClose();
  };

  const availableMethods = paymentMethods.filter(m => m.type === selectedMethod);

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => { if (!o) handleClose(); }}
      title="Add Funds to Wallet"
      maxWidth={500}
      footer={
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={handleClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleDeposit}
            disabled={!amount || processing || availableMethods.length === 0}
          >
            {processing ? 'Processing...' : 'Add Funds'}
          </Button>
        </div>
      }
    >
      <p className="muted" style={{ marginBottom: 20, fontSize: 13 }}>
        Enter the amount you would like to deposit to your wallet
      </p>

      {error && <Callout tone="warn" style={{ marginBottom: 14, fontSize: 13 }}>{error}</Callout>}
      {success && <Callout tone="ok" style={{ marginBottom: 14, fontSize: 13 }}>{success}</Callout>}

      {/* Amount input */}
      <div style={{ marginBottom: 20 }}>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 6, color: 'var(--ink-2)' }}>
          Amount
        </label>
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-3)', fontWeight: 500 }}>$</span>
          <input
            className="input"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            style={{ paddingLeft: 28 }}
          />
        </div>
      </div>

      {/* Method type selection */}
      <div style={{ marginBottom: 16 }}>
        <p style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink-2)', marginBottom: 10 }}>Payment Method Type</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {(['card', 'bank'] as PaymentMethodType[]).map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => { setSelectedMethod(type); setSelectedMethodId(null); }}
              style={{
                padding: 16,
                border: `2px solid ${selectedMethod === type ? 'var(--brand-blue)' : 'var(--line)'}`,
                borderRadius: 10,
                cursor: 'pointer',
                background: selectedMethod === type ? 'rgba(59,130,246,0.06)' : 'var(--bg-elev)',
                textAlign: 'center',
                transition: 'all 0.15s',
              }}
            >
              <Icon name={type === 'card' ? 'card' : 'building'} size={28} style={{ color: selectedMethod === type ? 'var(--brand-blue)' : 'var(--ink-3)', marginBottom: 6 }} />
              <div style={{ fontWeight: selectedMethod === type ? 600 : 400, fontSize: 14 }}>
                {type === 'card' ? 'Credit Card' : 'Bank Account'}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Available methods */}
      {availableMethods.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <p style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink-2)', marginBottom: 8 }}>
            Select {selectedMethod === 'card' ? 'Card' : 'Bank Account'}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {availableMethods.map((method) => (
              <button
                key={method.id}
                type="button"
                onClick={() => setSelectedMethodId(method.id)}
                style={{
                  padding: '12px 14px',
                  border: `2px solid ${selectedMethodId === method.id ? 'var(--brand-blue)' : 'var(--line)'}`,
                  borderRadius: 8,
                  cursor: 'pointer',
                  background: selectedMethodId === method.id ? 'rgba(59,130,246,0.06)' : 'var(--bg-elev)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  textAlign: 'left',
                  transition: 'all 0.15s',
                }}
              >
                <Icon name={selectedMethod === 'card' ? 'card' : 'building'} size={18} style={{ color: selectedMethodId === method.id ? 'var(--brand-blue)' : 'var(--ink-3)', flexShrink: 0 }} />
                <span style={{ flex: 1, fontWeight: 500, fontSize: 14 }}>{method.name} •••• {method.last4}</span>
                {method.isDefault && <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--brand-blue)' }}>Default</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {availableMethods.length === 0 && (
        <Callout tone="info" style={{ fontSize: 13 }}>
          No {selectedMethod === 'card' ? 'credit cards' : 'bank accounts'} found. Please add a payment method first.
        </Callout>
      )}
    </Dialog>
  );
};

export default DepositDialog;
